import { useCallback, useEffect, useRef, useState } from "react";
import type { Dispatch, RefObject, SetStateAction } from "react";
import { getFullNodePositions } from "../drawNode/drawNode";
import type { TreeNodeData } from "../TreeViewDataLayer";
const NODE_WIDTH = 260;
const NODE_HEIGHT = 172;
const FIT_PADDING = 0.8;

const collectHighlightedAboIds = (
  node: TreeNodeData,
  predicate: (node: TreeNodeData) => boolean,
  result: Set<number> = new Set(),
): Set<number> => {
  const hasHighlightedDescendant = collectHighlightedPaths(node, predicate, result);
  if (hasHighlightedDescendant) result.add(node.abo);
  return result;
};

const collectHighlightedPaths = (
  node: TreeNodeData,
  predicate: (node: TreeNodeData) => boolean,
  result: Set<number>,
): boolean => {
  const isSelf = predicate(node);
  if (isSelf) {
    result.add(node.abo);
  }
  // Always traverse children — a highlighted node may have highlighted descendants deeper in the tree
  let hasHighlighted = isSelf;
  node.downlines?.forEach((child) => {
    if (collectHighlightedPaths(child, predicate, result)) {
      result.add(child.abo);
      hasHighlighted = true;
    }
  });
  return hasHighlighted;
};

interface UseFitToViewProps {
  aboData: TreeNodeData | null;
  canvasSize: { width: number; height: number };
  setZoom: (zoom: number) => void;
  setPan: (pan: { x: number; y: number }) => void;
  setAboData?: Dispatch<SetStateAction<TreeNodeData | null>>;
  treeViewRootRef?: RefObject<HTMLElement | null>;
  /** Optional predicate to scope initial zoom/pan to highlighted nodes and their ancestors.
   *  If omitted, all nodes are used for bounds calculation. */
  highlightPredicate?: (node: TreeNodeData) => boolean;
  /** Optional signature (e.g. a string built from active filter flags) that triggers a
   *  re-fit whenever it changes. Filters can shrink or grow the visible node set, which
   *  would otherwise leave the view zoomed/panned for a tree that no longer exists. */
  filterSignature?: string;
}

export const useFitToView = ({
  aboData,
  canvasSize,
  setZoom,
  setPan,
  setAboData,
  treeViewRootRef,
  highlightPredicate,
  filterSignature,
}: UseFitToViewProps): { isReady: boolean; resetFit: () => void } => {
  const hasFitRef = useRef(false);
  const hasExpandedRef = useRef(false);
  const [isReady, setIsReady] = useState(false);
  const rootAboKeyRef = useRef<string | null>(null);
  const whosNewSignatureRef = useRef<string | null>(null);
  const filterSignatureRef = useRef<string | null>(null);
  // Bumped by resetFit() so the fit effect below re-runs even when canvasSize is
  // unchanged from the last fit (e.g. toggling fullscreen without an actual size
  // change) — canvasSize alone isn't a reliable trigger since hasFitRef/isReady
  // aren't effect dependencies.
  const [fitVersion, setFitVersion] = useState(0);

  // Allows callers (e.g. entering/exiting fullscreen) to force the tree to
  // re-fit to the current canvasSize instead of preserving the prior fit.
  const resetFit = useCallback(() => {
    hasFitRef.current = false;
    setIsReady(false);
    setFitVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    if (!aboData) return;
    const rootKey = `${aboData.aff}-${aboData.abo}`;
    const highlightedIds = highlightPredicate
      ? collectHighlightedAboIds(aboData, highlightPredicate)
      : new Set<number>();
    const highlightSignature = [...highlightedIds].sort().join(",");

    const rootChanged = rootKey !== rootAboKeyRef.current;
    const highlightChanged = highlightSignature !== whosNewSignatureRef.current;
    // Filters can shrink or grow the visible node set (e.g. hiding international downlines
    // or zero-volume ABOs), which would otherwise leave the view zoomed/panned for a tree
    // that no longer matches — so a filter change should re-fit just like a root change.
    const filterChanged = filterSignature !== undefined && filterSignature !== filterSignatureRef.current;

    if (rootChanged || highlightChanged || filterChanged) {
      rootAboKeyRef.current = rootKey;
      whosNewSignatureRef.current = highlightSignature;
      filterSignatureRef.current = filterSignature ?? null;
      hasFitRef.current = false;
      hasExpandedRef.current = false;
      setIsReady(false);
    }
  }, [aboData, highlightPredicate, filterSignature]);

  // Step 1: expand paths to highlighted nodes so aboData is correct before zoom calculation
  useEffect(() => {
    if (!aboData || hasFitRef.current || hasExpandedRef.current || !highlightPredicate || !setAboData) return;

    const highlightedIds = collectHighlightedAboIds(aboData, highlightPredicate);
    if (highlightedIds.size === 0) {
      hasExpandedRef.current = true;
      return;
    }

    // Set before calling setAboData to prevent this effect re-running on the subsequent render.
    // Step 2 is gated on aboData changing (it's a dependency), so it will only execute after
    // the expanded tree is committed — not with the pre-expansion tree.
    hasExpandedRef.current = true;
    setAboData((prev) => {
      if (!prev) return prev;
      const expandPaths = (node: TreeNodeData): TreeNodeData => ({
        ...node,
        expanded: highlightedIds.has(node.abo),
        downlines: node.downlines?.map(expandPaths),
      });
      return expandPaths(prev);
    });
  }, [aboData, highlightPredicate, setAboData]);

  // Step 2: calculate zoom/pan after expansion has been applied (or immediately if no predicate)
  useEffect(() => {
    if (!aboData || canvasSize.width === 0 || canvasSize.height === 0 || hasFitRef.current) return;
    // Wait for expansion to complete first when a predicate is set
    if (highlightPredicate && setAboData && !hasExpandedRef.current) return;

    const timeoutId = setTimeout(() => {
      const rootX = canvasSize.width / 2;
      const rootY = 80;

      // Use highlighted nodes (and their ancestors) for bounds if a predicate is provided,
      // otherwise fall back to all nodes
      const highlightedIds = highlightPredicate
        ? collectHighlightedAboIds(aboData, highlightPredicate)
        : new Set<number>();

      const positions = getFullNodePositions(aboData, rootX, rootY);
      if (positions.size === 0) return;

      const boundsPositions =
        highlightedIds.size > 0 ? new Map([...positions].filter(([abo]) => highlightedIds.has(abo))) : positions;

      const xs = [...boundsPositions.values()].map((p) => p.x);
      const ys = [...boundsPositions.values()].map((p) => p.y);

      const minX = Math.min(...xs) - NODE_WIDTH / 2;
      const maxX = Math.max(...xs) + NODE_WIDTH / 2;
      const minY = Math.min(...ys) - NODE_HEIGHT / 2;
      const bottomPadding = highlightedIds.size > 0 ? NODE_HEIGHT : NODE_HEIGHT / 2;
      const maxY = Math.max(...ys) + bottomPadding;

      const treeWidth = maxX - minX;
      const treeHeight = maxY - minY;

      const zoomX = canvasSize.width / treeWidth;
      const zoomY = canvasSize.height / treeHeight;
      const fitZoom = Math.min(zoomX, zoomY) * FIT_PADDING;

      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      const pan = {
        x: canvasSize.width / 2 - centerX,
        y: rootY - centerY,
      };

      setZoom(fitZoom);
      setPan(pan);
      hasFitRef.current = true;
      setIsReady(true);

      // Center the tree view (rather than aligning its top edge to the viewport top) so
      // the whole canvas - including the zoom controls pinned to its bottom - lands on
      // screen without the user having to scroll further. When the container is taller
      // than the viewport, "center" scrolls past its top edge, which is the correct
      // trade-off here: seeing the zoom controls immediately matters more than keeping
      // the list/tree toggle button in view.
      if (treeViewRootRef?.current) {
        treeViewRootRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 100);

    return () => clearTimeout(timeoutId);
  }, [aboData, canvasSize, setZoom, setPan, treeViewRootRef, highlightPredicate, setAboData, fitVersion]);

  return { isReady, resetFit };
};
